import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table, Button, Space, Tag, message, Card, Typography, Tooltip,
  Badge, Modal, Descriptions, Divider, Statistic, Row, Col, Input,
  Select, DatePicker, Space as AntSpace
} from 'antd';
import {
  FileTextOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ClockCircleOutlined, EyeOutlined, DownloadOutlined, ReloadOutlined,
  SearchOutlined, FilterOutlined, PrinterOutlined, FileExcelOutlined
} from '@ant-design/icons';
import { getIntegrationLogs, exportLogs } from '../../services/limsService';
import { useReactToPrint } from 'react-to-print';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const IntegrationLogsList = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const printRef = useRef();
  const [filters, setFilters] = useState({
    integration_type: '',
    status: '',
    search: '',
    dateRange: null
  });

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getIntegrationLogs(filters);
      console.log('Loaded integration logs:', data);
      console.log('Logs count:', data ? data.length : 0);
      setLogs(data || []);
    } catch (error) {
      message.error('Failed to load integration logs');
      console.error('Error loading logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: 'LIMS Integration Logs Report',
    pageStyle: `
      @media print {
        @page {
          margin: 1cm;
          size: A4;
        }
        body { 
          -webkit-print-color-adjust: exact;
          font-family: Arial, sans-serif;
          font-size: 12px;
          line-height: 1.4;
        }
        /* Hide regular display layout */
        .ant-card { display: none !important; }
        .ant-modal { display: none !important; }
        
        /* Show print layout */
        .print-layout { display: block !important; }
        
        /* Print table styling */
        table { 
          width: 100% !important;
          border-collapse: collapse !important;
          margin-top: 20px;
        }
        th, td {
          border: 1px solid #000 !important;
          padding: 6px 8px !important;
          text-align: left;
          vertical-align: top;
        }
        th {
          background: #f5f5f5 !important;
          font-weight: bold;
        }
      }
    `,
    onBeforePrint: () => {
      console.log('Printing logs:', logs);
      console.log('Logs count:', logs.length);
      if (!logs || logs.length === 0) {
        alert('No logs to print. Please check if logs are being generated or try adjusting your filters.');
        return false;
      }
    }
  });

  const handleExportExcel = () => {
    if (!logs || logs.length === 0) return;
    
    const data = [
      ['LIMS Integration Logs Report'],
      ['Generated on:', new Date().toLocaleString()],
      [''],
      ['ID', 'Timestamp', 'User', 'Type', 'Status', 'Description', 'Endpoint', 'Response Code', 'Processing Time']
    ];

    logs.forEach(log => {
      data.push([
        log.id || '',
        log.timestamp ? new Date(log.timestamp).toLocaleString() : '',
        log.user_username || 'System',
        log.integration_type || '',
        log.status || '',
        log.description || '',
        log.endpoint_url || '',
        log.response_code || '',
        log.processing_time ? `${log.processing_time}ms` : ''
      ]);
    });

    // Convert to CSV and download
    const csvContent = data.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `lims_integration_logs_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewDetails = (record) => {
    setSelectedLog(record);
    setDetailModalVisible(true);
  };

  const handleExport = async () => {
    try {
      await exportLogs();
      message.success('Logs exported successfully');
    } catch (error) {
      message.error('Failed to export logs');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS': return 'success';
      case 'FAILED': return 'error';
      case 'PENDING': return 'warning';
      case 'RETRY': return 'warning';
      case 'PARTIAL': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCESS': return <CheckCircleOutlined />;
      case 'FAILED': return <CloseCircleOutlined />;
      case 'PENDING': return <ClockCircleOutlined />;
      case 'RETRY': return <ReloadOutlined />;
      case 'PARTIAL': return <ClockCircleOutlined />;
      default: return <ClockCircleOutlined />;
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      'API_CALL': 'blue',
      'WEBHOOK': 'green',
      'PATIENT_SYNC': 'purple',
      'SAMPLE_REGISTRATION': 'orange',
      'RESULT_UPLOAD': 'cyan',
      'ERROR_HANDLING': 'red',
      'DATA_MAPPING': 'magenta',
      'VALIDATION': 'gold',
    };
    return colors[type] || 'default';
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'User',
      dataIndex: 'user_username',
      key: 'user_username',
      render: (text) => text || 'System',
    },
    {
      title: 'Type',
      dataIndex: 'integration_type',
      key: 'integration_type',
      render: (type) => (
        <Tag color={getTypeColor(type)}>
          {type.replace('_', ' ')}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: 'Endpoint',
      dataIndex: 'endpoint_url',
      key: 'endpoint_url',
      ellipsis: true,
      render: (url) => url ? (
        <Tooltip title={url}>
          <Text code>{url.split('/').pop()}</Text>
        </Tooltip>
      ) : '-',
    },
    {
      title: 'Response Code',
      dataIndex: 'response_code',
      key: 'response_code',
      render: (code) => code ? (
        <Badge 
          count={code} 
          style={{ 
            backgroundColor: code >= 200 && code < 300 ? '#52c41a' : 
                           code >= 400 ? '#ff4d4f' : '#faad14' 
          }} 
        />
      ) : '-',
    },
    {
      title: 'Processing Time',
      dataIndex: 'processing_time',
      key: 'processing_time',
      render: (time) => time ? `${time}s` : '-',
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
            title="View Details"
          />
        </Space>
      ),
    },
  ];

  // Calculate statistics
  const stats = {
    total: logs.length,
    successful: logs.filter(l => l.status === 'SUCCESS').length,
    failed: logs.filter(l => l.status === 'FAILED').length,
    pending: logs.filter(l => l.status === 'PENDING').length,
  };

  return (
    <>
      {/* Regular Display Layout */}
      <Card>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={4}>Integration Logs</Title>
            <Space className="no-print">
              <Button 
                icon={<PrinterOutlined />} 
                onClick={handlePrint}
                disabled={!logs || logs.length === 0}
                title={logs && logs.length > 0 ? `Print ${logs.length} logs` : 'No logs to print'}
              >
                Print {logs && logs.length > 0 ? `(${logs.length})` : ''}
              </Button>
              <Button 
                icon={<FileExcelOutlined />} 
                onClick={handleExportExcel}
                disabled={!logs || logs.length === 0}
                title={logs && logs.length > 0 ? `Export ${logs.length} logs to Excel` : 'No logs to export'}
              >
                Export Excel {logs && logs.length > 0 ? `(${logs.length})` : ''}
              </Button>
            </Space>
          </div>
          
          <Row gutter={16} style={{ marginTop: '16px' }}>
            <Col span={6}>
              <Statistic title="Total" value={stats.total} />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Successful" 
                value={stats.successful} 
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Failed" 
                value={stats.failed} 
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Pending" 
                value={stats.pending} 
                valueStyle={{ color: '#faad14' }}
              />
            </Col>
          </Row>
        </div>

        {/* Filters */}
        <Card size="small" style={{ marginBottom: '16px' }}>
          <AntSpace wrap>
            <Input
              placeholder="Search logs..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Integration Type"
              value={filters.integration_type}
              onChange={(value) => handleFilterChange('integration_type', value)}
              style={{ width: 150 }}
              allowClear
            >
              <Option value="API_CALL">API Call</Option>
              <Option value="WEBHOOK">Webhook</Option>
              <Option value="PATIENT_SYNC">Patient Sync</Option>
              <Option value="SAMPLE_REGISTRATION">Sample Registration</Option>
              <Option value="RESULT_UPLOAD">Result Upload</Option>
              <Option value="ERROR_HANDLING">Error Handling</Option>
              <Option value="DATA_MAPPING">Data Mapping</Option>
              <Option value="VALIDATION">Validation</Option>
            </Select>
            <Select
              placeholder="Status"
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              style={{ width: 120 }}
              allowClear
            >
              <Option value="SUCCESS">Success</Option>
              <Option value="FAILED">Failed</Option>
              <Option value="PENDING">Pending</Option>
              <Option value="RETRY">Retry</Option>
              <Option value="PARTIAL">Partial</Option>
            </Select>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => handleFilterChange('dateRange', dates)}
            />
            <Button icon={<ReloadOutlined />} onClick={loadLogs}>
              Refresh
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              Export
            </Button>
          </AntSpace>
        </Card>

        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          locale={{
            emptyText: 'No integration logs found. Try adjusting your filters or check if logs are being generated.'
          }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} logs`,
          }}
        />
      </Card>

      {/* Print Layout - Hidden by default, shown only when printing */}
      <div ref={printRef} className="print-layout" style={{ display: 'none' }}>
        {/* Print Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px', paddingBottom: '10px', borderBottom: '2px solid #000' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
            LIMS Integration Logs Report
          </h2>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
            Generated on: {new Date().toLocaleString()}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Total: {stats.total} | Successful: {stats.successful} | Failed: {stats.failed} | Pending: {stats.pending}
          </div>
        </div>

        {/* Print Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ background: '#f5f5f5' }}>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>ID</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>User</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>Type</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>Status</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>Description</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>Endpoint</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>Response Code</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>Processing Time</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {logs.map(log => (
              <tr key={log.id}>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{log.id}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{log.user_username || 'System'}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{log.integration_type?.replace('_', ' ')}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{log.status}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{log.description}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{log.endpoint_url || '-'}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{log.response_code || '-'}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{log.processing_time ? `${log.processing_time}s` : '-'}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        title="Integration Log Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>
        ]}
        width={1000}
      >
        {selectedLog && (
          <div>
            <Descriptions column={2} bordered>
              <Descriptions.Item label="ID" span={2}>
                {selectedLog.id}
              </Descriptions.Item>
              <Descriptions.Item label="User">
                {selectedLog.user_username || 'System'}
              </Descriptions.Item>
              <Descriptions.Item label="Integration Type">
                <Tag color={getTypeColor(selectedLog.integration_type)}>
                  {selectedLog.integration_type.replace('_', ' ')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(selectedLog.status)}>
                  {selectedLog.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                {selectedLog.description}
              </Descriptions.Item>
              <Descriptions.Item label="Endpoint URL">
                {selectedLog.endpoint_url || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="HTTP Method">
                {selectedLog.http_method || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Response Code">
                {selectedLog.response_code || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Processing Time">
                {selectedLog.processing_time ? `${selectedLog.processing_time}s` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Timestamp" span={2}>
                {new Date(selectedLog.timestamp).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>

            {selectedLog.error_message && (
              <>
                <Divider />
                <Title level={5}>Error Message</Title>
                <Text type="danger">{selectedLog.error_message}</Text>
              </>
            )}

            {selectedLog.request_data && (
              <>
                <Divider />
                <Title level={5}>Request Data</Title>
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: '12px', 
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '200px'
                }}>
                  {JSON.stringify(selectedLog.request_data, null, 2)}
                </pre>
              </>
            )}

            {selectedLog.response_data && (
              <>
                <Divider />
                <Title level={5}>Response Data</Title>
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: '12px', 
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '200px'
                }}>
                  {JSON.stringify(selectedLog.response_data, null, 2)}
                </pre>
              </>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default IntegrationLogsList;
